import { useEffect, useCallback, useRef } from "react";

const useAutoRefresh = (
  totalTimeMs = 3 * 60 * 60 * 1000,
  inactivityTimeMs = 5 * 60 * 1000
) => {
  const timeoutId = useRef<NodeJS.Timeout>();
  const initialExpirationRef = useRef(Date.now() + totalTimeMs);
  const lastInteractionExpirationRef = useRef(Date.now() + inactivityTimeMs);

  const scheduleRefresh = useCallback(() => {
    const nextExpiration = Math.max(
      initialExpirationRef.current,
      lastInteractionExpirationRef.current
    );

    clearTimeout(timeoutId.current);

    const timeRemaining = nextExpiration - Date.now();

    if (timeRemaining <= 0) {
      window.location.reload();
    } else {
      timeoutId.current = setTimeout(() => {
        window.location.reload();
      }, timeRemaining);
    }
  }, []);

  const resetTimer = useCallback(() => {
    lastInteractionExpirationRef.current = Date.now() + inactivityTimeMs;
    scheduleRefresh();
  }, [inactivityTimeMs, scheduleRefresh]);

  useEffect(() => {
    initialExpirationRef.current = Date.now() + totalTimeMs;
    scheduleRefresh();
  }, [totalTimeMs, scheduleRefresh]);

  useEffect(() => {
    const events = [
      "mousemove",
      "keydown",
      "mousedown",
      "touchstart",
      "click",
      "scroll",
    ];

    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    scheduleRefresh();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
      clearTimeout(timeoutId.current);
    };
  }, [resetTimer, scheduleRefresh]);
};

export default useAutoRefresh;
